import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthApi } from '@app/hooks/useApis';
import { useAuthSession } from '@app/context/auth-session';
import { buildLoginRedirectPath } from '@app/utils/auth-redirect';
import { resolveErrorMessage } from '@app/utils/errors';
import {
  DEFAULT_POST_LOGIN_REDIRECT_PATH,
  clearStoredSsoState,
  decodeSsoState,
  ensureSafeRedirectPath,
  readStoredSsoState
} from '@app/utils/sso';
import type { TokenGrantRequest } from '@app/types/auth';
import { toTokenRequest } from '@app/types/auth';
import type { TokenIssueResult, TokenNextStepPickOrgMembershipsInner } from '@app/types/sdk';
import { getTokenResponseOrThrow } from '@app/utils/tokens';

interface OrgPickerOption {
  orgId: string;
  orgName?: string | null;
}

const SsoCallbackPage = () => {
  const authApi = useAuthApi();
  const { setSessionTokens } = useAuthSession();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);
  const [orgOptions, setOrgOptions] = useState<OrgPickerOption[]>([]);
  const [orgSelection, setOrgSelection] = useState<string | null>(null);
  const [orgSubmitting, setOrgSubmitting] = useState(false);
  const [orgError, setOrgError] = useState<string | null>(null);
  const [pendingRequest, setPendingRequest] = useState<TokenGrantRequest | null>(null);
  const [redirectPath, setRedirectPath] = useState(DEFAULT_POST_LOGIN_REDIRECT_PATH);
  const automaticExchangeKeyRef = useRef<string | null>(null);

  const exchangeToken = useCallback(
    async (request: TokenGrantRequest, targetRedirectPath: string) => {
      setBusy(true);
      setError(null);
      setOrgOptions([]);
      setOrgSelection(null);
      setOrgError(null);
      setPendingRequest(null);
      try {
        const tokenResult = (await authApi.authTokenPost({ tokenRequest: toTokenRequest(request) })) as TokenIssueResult;

        if ('nextStep' in tokenResult && tokenResult.nextStep === 'pickOrg' && Array.isArray(tokenResult.memberships)) {
          const memberships = (tokenResult.memberships ?? []).map((membership: TokenNextStepPickOrgMembershipsInner) => ({
            orgId: membership.orgId,
            orgName: membership.orgName ?? membership.orgId
          }));
          setOrgOptions(memberships);
          setOrgSelection(memberships[0]?.orgId ?? null);
          setPendingRequest(request);
          setBusy(false);
          return;
        }

        const tokens = getTokenResponseOrThrow(tokenResult);
        await setSessionTokens(tokens);
        clearStoredSsoState();
        navigate(targetRedirectPath, { replace: true });
      } catch (caught: unknown) {
        const message = await resolveErrorMessage(caught, 'Failed to exchange identity handle');
        setError(message);
        setBusy(false);
      }
    },
    [authApi, navigate, setSessionTokens]
  );

  useEffect(() => {
    const queryError = searchParams.get('error');
    if (queryError) {
      clearStoredSsoState();
      setError(queryError);
      setBusy(false);
      return;
    }

    const oidcTx = searchParams.get('oidcTx');
    const samlTx = searchParams.get('samlTx');
    const loginTx = searchParams.get('loginTx');
    const emailTx = searchParams.get('emailTx');

    let grantType: 'oidcTx' | 'samlTx' | 'loginTx' | 'emailTx' | null = null;
    if (oidcTx) grantType = 'oidcTx';
    else if (samlTx) grantType = 'samlTx';
    else if (loginTx) grantType = 'loginTx';
    else if (emailTx) grantType = 'emailTx';

    if (!grantType) {
      setError('Missing auth handle in callback URL');
      setBusy(false);
      return;
    }

    const grantHandle = oidcTx ?? samlTx ?? loginTx ?? emailTx;
    const orgIdParam = searchParams.get('orgId') ?? undefined;
    const automaticExchangeKey = `${grantType}:${grantHandle ?? ''}:${orgIdParam ?? ''}`;
    if (automaticExchangeKeyRef.current === automaticExchangeKey) {
      return;
    }

    const stateParam = searchParams.get('state') ?? searchParams.get('relayState') ?? searchParams.get('RelayState');
    const storedState = readStoredSsoState();
    const decodedState = stateParam ? decodeSsoState(stateParam) : storedState?.value ?? null;

    if (decodedState && storedState && decodedState.nonce !== storedState.value.nonce) {
      console.warn('[sso] relay state mismatch', { decodedState, storedState });
      setError('We could not verify your single sign-on session. Start the sign-in process again.');
      clearStoredSsoState();
      setBusy(false);
      return;
    }

    const resolvedRedirectPath = decodedState?.redirectPath
      ? ensureSafeRedirectPath(decodedState.redirectPath)
      : ensureSafeRedirectPath(searchParams.get('redirect'));
    setRedirectPath(resolvedRedirectPath);

    let tokenRequest: TokenGrantRequest | null = null;

    switch (grantType) {
      case 'oidcTx':
        if (!oidcTx) {
          setError('Missing OIDC handle in callback URL');
          setBusy(false);
          return;
        }
        tokenRequest = {
          grantType: 'oidcTx',
          oidcTx,
          ...(orgIdParam ? { orgId: orgIdParam } : {})
        };
        break;
      case 'samlTx':
        if (!samlTx) {
          setError('Missing SAML handle in callback URL');
          setBusy(false);
          return;
        }
        tokenRequest = {
          grantType: 'samlTx',
          samlTx,
          ...(orgIdParam ? { orgId: orgIdParam } : {})
        };
        break;
      case 'loginTx':
        if (!loginTx) {
          setError('Missing login transaction in callback URL');
          setBusy(false);
          return;
        }
        tokenRequest = {
          grantType: 'loginTx',
          loginTx,
          ...(orgIdParam ? { orgId: orgIdParam } : {})
        };
        break;
      case 'emailTx':
        if (!emailTx) {
          setError('Missing email transaction in callback URL');
          setBusy(false);
          return;
        }
        tokenRequest = {
          grantType: 'emailTx',
          emailTx,
          ...(orgIdParam ? { orgId: orgIdParam } : {})
        };
        break;
      default:
        setError('Unsupported auth handle in callback URL');
        setBusy(false);
        return;
    }

    automaticExchangeKeyRef.current = automaticExchangeKey;
    clearStoredSsoState();
    void exchangeToken(tokenRequest, resolvedRedirectPath);
  }, [exchangeToken, searchParams]);

  const withOrgSelection = useCallback(
    (request: TokenGrantRequest, orgId: string): TokenGrantRequest => {
      switch (request.grantType) {
        case 'emailTx':
        case 'loginTx':
        case 'oidcTx':
        case 'samlTx':
        case 'refresh':
          return { ...request, orgId };
        default:
          return request;
      }
    },
    []
  );

  const handleOrgSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!pendingRequest || !orgSelection) {
        setOrgError('Select an organization to continue.');
        return;
      }
      setOrgSubmitting(true);
      setOrgError(null);
      try {
        await exchangeToken(withOrgSelection(pendingRequest, orgSelection), redirectPath);
      } finally {
        setOrgSubmitting(false);
      }
    },
    [exchangeToken, orgSelection, pendingRequest, redirectPath, withOrgSelection]
  );

  const orgOptionsView = useMemo(() => {
    if (orgOptions.length === 0) {
      return null;
    }
    return (
      <main className="auth-layout">
        <section className="auth-card">
          <header className="auth-card__header">
            <h1 className="auth-title">Choose an organization</h1>
            <p className="auth-subtitle">This single sign-on session can access multiple organizations.</p>
          </header>
          <form className="auth-form" onSubmit={handleOrgSubmit}>
            <div className="auth-field">
              <label htmlFor="org-picker">Organization</label>
              <select
                id="org-picker"
                value={orgSelection ?? ''}
                onChange={(event) => setOrgSelection(event.target.value)}
              >
                {orgOptions.map((option) => (
                  <option key={option.orgId} value={option.orgId}>
                    {option.orgName ?? option.orgId}
                  </option>
                ))}
              </select>
            </div>
            {orgError && (
              <p className="auth-error" role="alert">
                {orgError}
              </p>
            )}
            <button type="submit" className="auth-submit" disabled={orgSubmitting || !orgSelection}>
              {orgSubmitting ? 'Signing in…' : 'Continue'}
            </button>
            <button
              type="button"
              className="auth-ghost"
              onClick={() => navigate(buildLoginRedirectPath({ pathname: redirectPath }))}
              disabled={orgSubmitting}>
              Cancel
            </button>
          </form>
        </section>
      </main>
    );
  }, [handleOrgSubmit, navigate, orgError, orgOptions, orgSelection, orgSubmitting, redirectPath]);

  if (orgOptionsView) {
    return orgOptionsView;
  }

  if (busy && !error) {
    return (
      <main className="auth-layout">
        <section className="auth-card">
          <header className="auth-card__header">
            <h1 className="auth-title">Completing single sign-on…</h1>
            <p className="auth-subtitle">Hold tight while we exchange your identity handle for access tokens.</p>
          </header>
        </section>
      </main>
    );
  }

  if (error) {
    return (
      <main className="auth-layout">
        <section className="auth-card">
          <header className="auth-card__header">
            <h1 className="auth-title">Unable to complete sign-in</h1>
            <p className="auth-error" role="alert">
              {error}
            </p>
            <button type="button" className="auth-submit" onClick={() => navigate(buildLoginRedirectPath({ pathname: redirectPath }))}>
              Start over
            </button>
          </header>
        </section>
      </main>
    );
  }

  return null;
};

export default SsoCallbackPage;
